import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isEmail, readWaitlistParams, rememberInvite, rememberedInvite, inviteLink, boardingPassLink, storeLinks,
  joinErrorText, joinWaitlist, confirmWaitlist, unsubscribeWaitlist, trackHoldingStep,
} from '../../lib/waitlist';
import { SHIP_MARKUP, mountHeroSky, mountFirstLight } from './holdingSky';
import roomCabin from '../../assets/holding/room-cabin.jpg';
import roomHiggs from '../../assets/holding/room-higgs.jpg';
import roomNature from '../../assets/holding/room-nature.jpg';
import roomGreenhouse from '../../assets/holding/room-greenhouse.jpg';
import roomQuarters from '../../assets/holding/room-quarters.jpg';
import roomSky from '../../assets/holding/room-sky.jpg';
import shipWide from '../../assets/holding/ship-wide.jpg';
import shipPhone from '../../assets/holding/ship-phone.jpg';
import travelTogether from '../../assets/holding/travel-together.jpg';
import talpulaLetter from '../../assets/holding/talpula-letter.jpg';
import leavingOlaris from '../../assets/holding/leaving-olaris.jpg';
import astrolumeVocals from '../../assets/holding/astrolume-vocals.mp3';
import './holding.css';

// The launch holding page (user decisions, 13 Oct 2026; prototype and the art
// prompts in docs/holding-page-prototype/). PreviewGate shows it to web
// visitors without preview access. The ship is about to pass by and visitors
// save a place aboard: founding travellers get First Light, letters from the
// ship and an invitation to bring someone. Sign-up is double opt-in through
// the server functions in src/lib/waitlist.js; the invitation appears only
// after confirming. Music (Astrolume, with vocals) plays only when asked.

// Her line comes from the media CDN once it is set (src/lib/mediaResolver.ts).
// Not through mediaUrl: that would put the whole media list in this public bundle.
const VOICE_SRC = import.meta.env.VITE_MEDIA_BASE
  ? `${String(import.meta.env.VITE_MEDIA_BASE).replace(/\/+$/, '')}/SacredMothr/meditation/company-1.mp3`
  : `${import.meta.env.BASE_URL}SacredMothr/meditation/company-1.mp3`;
const MUSIC_VOL = 0.35;
const DWELL_MS = 7000;
const SHIP_LINES = ['I am glad you came.', 'There is room aboard for you.', 'I have kept a light on.', 'Rest here a while, traveller.'];

const ROOMS = [
  { name: 'The cabin', img: roomCabin, pos: '42% 50%', alt: 'A dark, warm study with a window onto a violet nebula', text: 'Talk with SacredMothr at any hour. She remembers what matters to you, and she notices the hard days.' },
  { name: 'The Higgs Chamber', img: roomHiggs, pos: '50% 50%', alt: 'A long corridor with a spa and training area, stars at the far window', text: 'Guided workouts at three gravities, with Talpula counting every rep out loud. Red light and cold water too.' },
  { name: 'The meditation room', img: roomNature, pos: '40% 50%', alt: 'The Nature chamber: ancient trees growing through the curved walls of the ship', text: 'Sit among the trees of the Nature chamber. Tell her how you feel and she will guide you, or head outside for a walk under open sky.' },
  { name: 'The greenhouse', img: roomGreenhouse, pos: '50% 50%', alt: 'Tiers of glowing plants under a glass dome full of stars', text: 'Every entry in your ledger plants something. A hard day grows a Frost Bloom, and its memory is kept.' },
  { name: 'The Sleep Quarters', img: roomQuarters, pos: '35% 50%', alt: 'A soft, curved bedroom with a nebula window', text: 'Wind down with sleep sounds, a glowing aquarium and a gentle alarm at your usual hour.' },
  { name: 'The Resilience Sky', img: roomSky, pos: '50% 40%', alt: 'An observation dome under the Milky Way', text: 'Each thing you do for yourself lights a star. Stars only ever brighten.' },
];

const prefersReducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

function PlayIcon({ playing }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      {playing ? <path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" /> : <path d="M4 2.5v11l9-5.5z" />}
    </svg>
  );
}

function InviteBox({ code }) {
  const [copied, setCopied] = useState('Copy link');
  const inputRef = useRef(null);
  const link = inviteLink(code);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied('Copied'); }
    catch { inputRef.current?.select(); setCopied('Selected'); }
  };
  return (
    <div className="invite">
      <b>Bring someone aboard</b>
      <p>Share your invitation. When they join, your two founding stars are drawn side by side.</p>
      <div className="linkrow">
        <input ref={inputRef} readOnly aria-label="Your invitation link" value={link} onFocus={(e) => e.target.select()} />
        <button type="button" onClick={copy}>{copied}</button>
      </div>
    </div>
  );
}

// The founding traveller's boarding pass: claims First Light for an account
// whose email differs from this one (or uses Apple's Hide My Email).
function BoardingPass({ pass }) {
  const [copied, setCopied] = useState('Copy link');
  const inputRef = useRef(null);
  const link = boardingPassLink(pass);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied('Copied'); }
    catch { inputRef.current?.select(); setCopied('Selected'); }
  };
  return (
    <div className="invite">
      <b>Your boarding pass</b>
      <p>Keep this link to yourself. If you sign in to the app with a different email, or with Apple's Hide My Email, open it once you are aboard and your First Light will find you.</p>
      <div className="linkrow">
        <input ref={inputRef} readOnly aria-label="Your boarding pass link" value={link} onFocus={(e) => e.target.select()} />
        <button type="button" onClick={copy}>{copied}</button>
      </div>
    </div>
  );
}

// The sign-up, in the hero and again at the end. Both show the same state.
function Signup({ id, state, onSubmit }) {
  const [email, setEmail] = useState('');
  if (state.confirm?.status === 'confirmed') {
    return (
      <div className="signup">
        <div className="done" role="status"><i className="spark" /><div><b>Your place is confirmed.</b><p>First Light is set aside in your sky. We will write a few letters from the ship before launch.</p></div></div>
        <InviteBox code={state.confirm.code} />
        {state.confirm.pass && <BoardingPass pass={state.confirm.pass} />}
      </div>
    );
  }
  if (state.join.status === 'sent') {
    return (
      <div className="signup">
        <div className="done" role="status"><i className="spark" /><div><b>Check your inbox.</b><p>Tap the link in our email to confirm your place. Your invitation to bring someone aboard comes with it.</p></div></div>
      </div>
    );
  }
  const sending = state.join.status === 'sending';
  return (
    <div className="signup">
      <form className="field" noValidate onSubmit={(e) => { e.preventDefault(); onSubmit(email); }}>
        <input
          id={id}
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Your email address"
          aria-label="Email address"
          aria-invalid={state.join.status === 'error' || undefined}
          value={email}
          disabled={sending}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="go" type="submit" disabled={sending}>{sending ? 'Saving…' : 'Save my place'}</button>
      </form>
      {state.join.status === 'error' && <p className="error" role="alert">{state.join.error}</p>}
      <p className="fine">A few letters from the ship before launch, then one on launch day. Unsubscribe any time.</p>
    </div>
  );
}

export default function HoldingPage() {
  const [join, setJoin] = useState({ status: 'idle', error: '' });
  const [confirm, setConfirm] = useState(null); // { status: 'confirmed', code }
  const [notice, setNotice] = useState(null); // a line shown at the top (unsubscribe, unknown link)
  const [showUnsubHelp, setShowUnsubHelp] = useState(false);
  const stores = storeLinks();

  // ---- links from the emails and invitations: read once as the page opens
  // (so a second run of the effect still sees them), then tidied from the
  // address bar. Confirming and unsubscribing twice is harmless.
  const [links] = useState(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(search);
    return { ...readWaitlistParams(search), had: params.has('invite') || params.has('confirm') || params.has('unsubscribe') };
  });
  useEffect(() => {
    trackHoldingStep('shown');
    if (links.invite) rememberInvite(links.invite);
    if (links.had) {
      try { window.history.replaceState({}, '', window.location.pathname + window.location.hash); } catch { /* not allowed */ }
    }
    if (links.confirm) {
      confirmWaitlist(links.confirm)
        .then((place) => {
          if (place) { setConfirm({ status: 'confirmed', code: place.code, pass: place.pass }); trackHoldingStep('confirmed'); }
          else setNotice('That confirmation link has expired or was already removed. Save your place again below.');
        })
        .catch(() => setNotice('We could not confirm your place just now. Please open the link in our email again in a moment.'));
    }
    if (links.unsubscribe) {
      unsubscribeWaitlist(links.unsubscribe)
        .then(() => setNotice('Your address has been removed from the list. We will not write to you again. If you confirmed your place, your First Light is still kept for you.'))
        .catch(() => setNotice('We could not remove your address just now. Please open the link in our email again in a moment.'));
    }
  }, [links]);

  const submit = useCallback(async (raw) => {
    const email = String(raw || '').trim();
    if (!isEmail(email)) { setJoin({ status: 'error', error: joinErrorText({ message: 'waitlist_email' }) }); return; }
    setJoin({ status: 'sending', error: '' });
    try {
      await joinWaitlist(email, rememberedInvite());
      setJoin({ status: 'sent', error: '' });
      trackHoldingStep('signup');
    } catch (err) {
      setJoin({ status: 'error', error: joinErrorText(err) });
    }
  }, []);
  const signupState = { join, confirm };

  // ---- music and her voice
  const musicRef = useRef(null);
  const voiceRef = useRef(null);
  const fadeRef = useRef(0);
  const heldByVoiceRef = useRef(false);
  const [musicOn, setMusicOn] = useState(false);
  const musicOnRef = useRef(false);
  const [musicNote, setMusicNote] = useState('Astrolume');
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [voiceNote, setVoiceNote] = useState('Eight seconds · sound on');

  const fadeTo = useCallback((target, ms, done) => {
    const m = musicRef.current; if (!m) return;
    clearInterval(fadeRef.current);
    const from = m.volume, steps = Math.max(1, Math.round(ms / 50)); let n = 0;
    fadeRef.current = setInterval(() => {
      n++; m.volume = Math.min(1, Math.max(0, from + (target - from) * (n / steps)));
      if (n >= steps) { clearInterval(fadeRef.current); done?.(); }
    }, 50);
  }, []);
  const setOn = (on) => { musicOnRef.current = on; setMusicOn(on); };

  const startMusic = useCallback(() => {
    const m = musicRef.current; if (!m) return;
    setOn(true);
    setMusicNote('Astrolume');
    trackHoldingStep('sound_on');
    // Her voice is playing: the music waits until she has finished.
    if (voiceRef.current && !voiceRef.current.paused) { heldByVoiceRef.current = true; return; }
    m.volume = 0;
    m.play().then(() => fadeTo(MUSIC_VOL, 3000)).catch(() => { setOn(false); setMusicNote('Tap to try again'); });
  }, [fadeTo]);
  const stopMusic = useCallback(() => {
    setOn(false);
    heldByVoiceRef.current = false;
    fadeTo(0, 900, () => musicRef.current?.pause());
  }, [fadeTo]);

  useEffect(() => {
    const onVis = () => {
      const m = musicRef.current;
      if (!m || !musicOnRef.current) return;
      if (document.hidden) m.pause();
      else if (!heldByVoiceRef.current) { m.play().catch(() => {}); fadeTo(MUSIC_VOL, 1200); }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); clearInterval(fadeRef.current); };
  }, [fadeTo]);

  const toggleVoice = () => {
    const v = voiceRef.current; if (!v) return;
    if (v.paused) { v.currentTime = 0; v.play().catch(() => setVoiceNote('Could not play here')); }
    else v.pause();
  };
  const onVoicePlay = () => {
    setVoicePlaying(true); setVoiceNote('Playing');
    if (musicOnRef.current && musicRef.current && !musicRef.current.paused) {
      heldByVoiceRef.current = true;
      fadeTo(0, 500, () => musicRef.current?.pause());
    }
  };
  const onVoiceStop = () => {
    setVoicePlaying(false); setVoiceNote('Eight seconds · sound on');
    if (heldByVoiceRef.current) {
      heldByVoiceRef.current = false;
      const m = musicRef.current;
      if (musicOnRef.current && m) {
        if (m.paused) m.volume = 0;
        m.play().then(() => fadeTo(MUSIC_VOL, 1500)).catch(() => {});
      }
    }
  };
  const onVoiceMeta = (e) => {
    const s = Math.round(e.currentTarget.duration);
    if (Number.isFinite(s) && s > 0) setVoiceNote(`${s} seconds · sound on`);
  };

  // ---- the hero sky and the ship
  const heroCanvas = useRef(null);
  const flightRef = useRef(null);
  const shipRef = useRef(null);
  const [whisper, setWhisper] = useState({ text: '', on: false, told: false });
  const lineRef = useRef(0);
  const whisperTimer = useRef(0);
  useEffect(() => {
    if (!heroCanvas.current || !flightRef.current || !shipRef.current) return undefined;
    const stop = mountHeroSky(heroCanvas.current, { flight: flightRef.current, ship: shipRef.current });
    const onMove = (e) => {
      if (e.pointerType !== 'mouse' || !flightRef.current) return;
      const r = flightRef.current.getBoundingClientRect();
      const near = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2)) < 90;
      flightRef.current.classList.toggle('near', near);
    };
    window.addEventListener('pointermove', onMove);
    return () => { stop(); window.removeEventListener('pointermove', onMove); clearTimeout(whisperTimer.current); };
  }, []);
  const tapShip = () => {
    if (!musicOnRef.current) startMusic();
    setWhisper({ text: SHIP_LINES[lineRef.current++ % SHIP_LINES.length], on: true, told: true });
    clearTimeout(whisperTimer.current);
    whisperTimer.current = setTimeout(() => setWhisper(w => ({ ...w, on: false })), 3600);
  };

  // ---- First Light
  const firstLightRef = useRef(null);
  useEffect(() => (firstLightRef.current ? mountFirstLight(firstLightRef.current) : undefined), []);

  // ---- the tour of the rooms
  const [room, setRoom] = useState(0);
  const [held, setHeld] = useState(false);
  const [tourInView, setTourInView] = useState(false);
  const windowRef = useRef(null);
  const swipeX = useRef(null);
  const reduced = prefersReducedMotion();
  useEffect(() => {
    const el = windowRef.current;
    if (!el || typeof IntersectionObserver !== 'function') return undefined;
    const io = new IntersectionObserver(([e]) => setTourInView(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (reduced || held || !tourInView) return undefined;
    const t = setTimeout(() => setRoom(r => (r + 1) % ROOMS.length), DWELL_MS);
    return () => clearTimeout(t);
  }, [room, held, tourInView, reduced]);
  const goRoom = (i) => setRoom(((i % ROOMS.length) + ROOMS.length) % ROOMS.length);
  const navRefs = useRef([]);
  const onTourKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const next = ((room + (e.key === 'ArrowRight' ? 1 : -1)) + ROOMS.length) % ROOMS.length;
    goRoom(next);
    navRefs.current[next]?.focus();
  };

  return (
    <div className="holding">
      <button className="sound" type="button" aria-pressed={musicOn} onClick={() => (musicOn ? stopMusic() : startMusic())}>
        <span className="bars" aria-hidden="true"><i /><i /><i /><i /></span>
        <span className="label"><b>{musicOn ? 'Sound off' : 'Sound on'}</b><small>{musicNote}</small></span>
      </button>
      <audio ref={musicRef} src={astrolumeVocals} preload="none" loop />

      <header className="hero" id="top">
        <canvas ref={heroCanvas} aria-hidden="true" />
        <div className={`flight${whisper.on ? ' speaking near' : ''}${whisper.told ? ' told' : ''}`} ref={flightRef}>
          <p className="whisper" aria-live="polite">{whisper.text}</p>
          <button className="ship-btn" type="button" aria-label="Say hello to SacredMothr" onClick={tapShip}>
            <div className="ship" ref={shipRef} aria-hidden="true" dangerouslySetInnerHTML={{ __html: SHIP_MARKUP }} />
          </button>
          <span className="tap-hint">Tap the ship</span>
        </div>

        <div className="wrap">
          {notice && <div className="done" role="status"><i className="spark" /><div><p>{notice}</p></div></div>}
          <p className="wordmark"><span className="name">Sacred<em>Mothr</em></span><span className="pill">Passing by soon</span></p>
          <h1>A calm companion for sleep, movement and <em>hard days.</em></h1>
          <p className="lede">A sanctuary ship is about to pass close by. Save your place aboard and a founding star will be waiting in your sky.</p>
          <Signup id="email-top" state={signupState} onSubmit={submit} />
        </div>
      </header>

      <main>
        <section className="voice" aria-label="SacredMothr's voice">
          <div className="wrap">
            <div>
              <p className="eyebrow">Her voice</p>
              <blockquote style={{ marginTop: 16 }}>I am glad you came. <span>You do not have to feel better to sit here.</span></blockquote>
              <cite>SacredMothr, in the meditation room</cite>
            </div>
            <button className={`listen${voicePlaying ? ' playing' : ''}`} type="button" onClick={toggleVoice}>
              <i className="disc"><PlayIcon playing={voicePlaying} /></i>
              <span>Listen<small>{voiceNote}</small></span>
            </button>
            <audio ref={voiceRef} src={VOICE_SRC} preload="metadata" onPlay={onVoicePlay} onPause={onVoiceStop} onEnded={onVoiceStop} onLoadedMetadata={onVoiceMeta} />
          </div>
        </section>

        <section className={`tour${held || !tourInView ? ' paused' : ''}`} aria-labelledby="rooms-title" onKeyDown={onTourKey} style={{ '--dwell': `${DWELL_MS / 1000}s` }}>
          <div className="wrap">
            <div className="section-head">
              <p className="eyebrow">Aboard</p>
              <h2 id="rooms-title">Take a look around</h2>
              <p>Each room looks after a different part of you. Wander between them at your own pace.</p>
            </div>
            <div
              className="window"
              ref={windowRef}
              onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHeld(true); }}
              onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHeld(false); }}
              onPointerDown={(e) => { swipeX.current = e.clientX; }}
              onPointerUp={(e) => {
                if (swipeX.current === null) return;
                const dx = e.clientX - swipeX.current; swipeX.current = null;
                if (Math.abs(dx) > 40) goRoom(room + (dx < 0 ? 1 : -1));
              }}
            >
              {ROOMS.map((r, i) => (
                <figure key={r.name} className={`slide${i === room ? ' on' : ''}`} aria-hidden={i !== room} aria-label={`${i + 1} of ${ROOMS.length}: ${r.name}`}>
                  <img src={r.img} alt={r.alt} style={{ objectPosition: r.pos }} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" />
                  <figcaption className="caption"><h3>{r.name}</h3><p>{r.text}</p></figcaption>
                </figure>
              ))}
            </div>
            <nav className="rooms-nav" aria-label="Rooms">
              {ROOMS.map((r, i) => (
                <button
                  key={r.name}
                  ref={(el) => { navRefs.current[i] = el; }}
                  type="button"
                  className={i < room ? 'seen' : undefined}
                  aria-current={i === room ? 'true' : undefined}
                  onClick={() => goRoom(i)}
                >
                  <i><b /></i>{r.name}
                </button>
              ))}
            </nav>
          </div>
        </section>

        <section className="founding" aria-labelledby="founding-title">
          <div className="wrap">
            <div className="section-head">
              <p className="eyebrow">Founding travellers</p>
              <h2 id="founding-title">Be waiting when she passes</h2>
              <p>Everyone who saves a place before launch is a founding traveller. Three things come with it.</p>
            </div>
            <div className="found-grid">
              <article className="star-card">
                <canvas ref={firstLightRef} aria-hidden="true" />
                <div className="star-copy">
                  <p className="tag">A landmark in your Resilience Sky</p>
                  <h3>First Light</h3>
                  <p className="story">You were waiting when she passed. She has not forgotten.</p>
                  <p>A star kept only for founding travellers. It shines from your first night aboard, it never fades, and nobody can earn it later.</p>
                </div>
              </article>
              <div className="found-side">
                <article className="aboard">
                  <figure className="aboard-art"><img src={travelTogether} alt="Two small gold moth-shaped ships flying side by side through a violet nebula, their trails of light twisting together" loading="lazy" decoding="async" /></figure>
                  <p className="tag">Bring someone aboard</p>
                  <h3>Travel together</h3>
                  <p>Once your place is confirmed you get your own invitation. When someone joins with it, your two founding stars are drawn side by side, and on launch day you can choose to come aboard together.</p>
                </article>
                <article className="letter">
                  <figure className="aboard-art"><img src={talpulaLetter} alt="Talpula, the ship's small brass droid with one glowing blue eye, hovering over a handwritten letter in a cosy study with a round window full of stars" style={{ objectPosition: '40% 50%' }} loading="lazy" decoding="async" /></figure>
                  <div>
                    <p className="tag">Letters from the ship</p>
                    <h3>Nearly within reach</h3>
                    <p className="excerpt">The Sun grows a little brighter in the forward window each night. We cannot slow for long, so Talpula has polished the greenhouse glass twice this week, and he wants you to know he has saved you a seat by the ferns.</p>
                    <p className="sig">SacredMothr, letter one</p>
                    <p className="small">A few letters before launch, with notes from Talpula, his sketches and a recording or two.</p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="voyage" aria-labelledby="voyage-title">
          <div className="wrap">
            <figure className="olaris">
              <img src={leavingOlaris} alt="A vast crimson giant sun hanging over a quiet world of pale stone terraces and still water, while a tiny gold moth-shaped ship rises away into the dark" loading="lazy" decoding="async" />
              <figcaption>Leaving Olaris</figcaption>
            </figure>
            <div>
              <p className="eyebrow">The Long Voyage</p>
              <h2 id="voyage-title" style={{ marginTop: 14 }}>A ship that has come a very long way</h2>
            </div>
            <div className="story">
              <p>The SacredMothr is a sanctuary ship from Olaris, a world lost when its sun swelled slowly into a crimson giant. Its people poured themselves into the ship, and she carries their voices still. Out in the dark she found travellers, and took them in. Now she is passing close to the Sun, and there is room aboard for you.</p>
              <p>As you look after yourself, the ship's six systems hold steady and the voyage moves on: a jump every four weeks, a new chapter with each one, and havens waiting along the way.</p>
              <p className="legs"><span><b>6</b> ship systems</span><span><b>4</b> weeks a jump</span><span><b>No</b> final chapter</span></p>
            </div>
          </div>
        </section>

        <section className="promises" aria-labelledby="promises-title">
          <div className="wrap">
            <div className="section-head">
              <p className="eyebrow">How we look after you</p>
              <h2 id="promises-title">Gentle by design</h2>
            </div>
            <ul className="promise-list">
              <li><h3>No streaks</h3><p>Nothing is lost if you miss a day. A quiet week simply counts as quiet.</p></li>
              <li><h3>Your readings stay with you</h3><p>Heart, sleep and step readings from Apple Health or Health Connect never leave your phone. Only weekly summaries are kept.</p></li>
              <li><h3>Help on every screen</h3><p>The SOS button is always one tap away, with grounding and crisis lines.</p></li>
              <li><h3>Real travellers only</h3><p>Every signal from a sister ark comes from a real person on the same voyage.</p></li>
            </ul>
          </div>
        </section>

        <section className="closing" aria-labelledby="closing-title">
          <figure className="art" aria-hidden="true">
            <picture>
              <source media="(max-width: 600px)" srcSet={shipPhone} />
              <img src={shipWide} alt="" loading="lazy" decoding="async" />
            </picture>
          </figure>
          <div className="wrap">
            <p className="eyebrow">iPhone, Android and the web</p>
            <h2 id="closing-title">Save your place aboard</h2>
            <p className="lede">Founding travellers get First Light, letters from the ship, and a place beside whoever they bring.</p>
            <Signup id="email-bottom" state={signupState} onSubmit={submit} />
            {(stores.apple || stores.google) && (
              <div className="stores">
                {stores.apple && <a className="store" href={stores.apple} target="_blank" rel="noopener noreferrer"><small>Pre-order on the</small><b>App Store</b></a>}
                {stores.google && <a className="store" href={stores.google} target="_blank" rel="noopener noreferrer"><small>Pre-register on</small><b>Google Play</b></a>}
              </div>
            )}
            <footer>
              <nav aria-label="Legal">
                <a href="#/terms">Terms of Service</a>
                <a href="#/privacy">Privacy Policy</a>
                <a href="#/contact">Contact</a>
                <button type="button" className="footlink" aria-expanded={showUnsubHelp} onClick={() => setShowUnsubHelp(s => !s)}>Unsubscribe</button>
              </nav>
              <span>SacredMothr</span>
              {showUnsubHelp && (
                <p className="unsub-help">Every email from us has an Unsubscribe link at the bottom, which removes your address straight away. Lost the email? Write to privacy@sacredmothr.com and we will remove it for you.</p>
              )}
            </footer>
          </div>
        </section>
      </main>
    </div>
  );
}
